import fr from './fr.json';
import en from './en.json';
import de from './de.json';
import es from './es.json';
import pt from './pt.json';
import it from './it.json';
import ar from './ar.json';
import lb from './lb.json';
import bg from './bg.json';
// Worldwide languages
import zh from './zh.json';
import hi from './hi.json';
import ja from './ja.json';
import ko from './ko.json';
import ru from './ru.json';
import tr from './tr.json';
import nl from './nl.json';
import pl from './pl.json';

export const messages = { 
  fr, en, de, es, pt, it, ar, lb, bg,
  zh, hi, ja, ko, ru, tr, nl, pl
};

// Primary supported locales with full translations
export const SUPPORTED_LOCALES = [
  { code: 'fr', name: 'Français', flag: '🇫🇷', dir: 'ltr' },
  { code: 'en', name: 'English', flag: '🇬🇧', dir: 'ltr' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', dir: 'ltr' },
  { code: 'es', name: 'Español', flag: '🇪🇸', dir: 'ltr' },
  { code: 'pt', name: 'Português', flag: '🇵🇹', dir: 'ltr' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹', dir: 'ltr' },
  { code: 'bg', name: 'Български', flag: '🇧🇬', dir: 'ltr' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦', dir: 'rtl' },
  { code: 'lb', name: 'Lëtzebuergesch', flag: '🇱🇺', dir: 'ltr' }
];

// Extended worldwide languages - fallback to English with browser auto-translate hint
export const WORLDWIDE_LOCALES = [
  // Primary (full support)
  ...SUPPORTED_LOCALES,
  // Extended (fallback to English, browser can auto-translate)
  { code: 'zh', name: '中文', flag: '🇨🇳', dir: 'ltr', fallback: 'en' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳', dir: 'ltr', fallback: 'en' },
  { code: 'ja', name: '日本語', flag: '🇯🇵', dir: 'ltr', fallback: 'en' },
  { code: 'ko', name: '한국어', flag: '🇰🇷', dir: 'ltr', fallback: 'en' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺', dir: 'ltr', fallback: 'en' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷', dir: 'ltr', fallback: 'en' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱', dir: 'ltr', fallback: 'en' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱', dir: 'ltr', fallback: 'en' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳', dir: 'ltr', fallback: 'en' },
  { code: 'th', name: 'ไทย', flag: '🇹🇭', dir: 'ltr', fallback: 'en' },
  { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩', dir: 'ltr', fallback: 'en' },
  { code: 'ms', name: 'Bahasa Melayu', flag: '🇲🇾', dir: 'ltr', fallback: 'en' },
  { code: 'he', name: 'עברית', flag: '🇮🇱', dir: 'rtl', fallback: 'en' },
  { code: 'fa', name: 'فارسی', flag: '🇮🇷', dir: 'rtl', fallback: 'ar' },
  { code: 'uk', name: 'Українська', flag: '🇺🇦', dir: 'ltr', fallback: 'en' },
  { code: 'ro', name: 'Română', flag: '🇷🇴', dir: 'ltr', fallback: 'en' },
  { code: 'el', name: 'Ελληνικά', flag: '🇬🇷', dir: 'ltr', fallback: 'en' },
  { code: 'cs', name: 'Čeština', flag: '🇨🇿', dir: 'ltr', fallback: 'en' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪', dir: 'ltr', fallback: 'en' },
  { code: 'da', name: 'Dansk', flag: '🇩🇰', dir: 'ltr', fallback: 'en' },
  { code: 'fi', name: 'Suomi', flag: '🇫🇮', dir: 'ltr', fallback: 'en' },
  { code: 'no', name: 'Norsk', flag: '🇳🇴', dir: 'ltr', fallback: 'en' },
  { code: 'hu', name: 'Magyar', flag: '🇭🇺', dir: 'ltr', fallback: 'en' },
  { code: 'bg', name: 'Български', flag: '🇧🇬', dir: 'ltr', fallback: 'en' },
  { code: 'bn', name: 'বাংলা', flag: '🇧🇩', dir: 'ltr', fallback: 'en' },
  { code: 'ta', name: 'தமிழ்', flag: '🇮🇳', dir: 'ltr', fallback: 'en' },
  { code: 'te', name: 'తెలుగు', flag: '🇮🇳', dir: 'ltr', fallback: 'en' },
  { code: 'mr', name: 'मराठी', flag: '🇮🇳', dir: 'ltr', fallback: 'en' },
  { code: 'sw', name: 'Kiswahili', flag: '🇰🇪', dir: 'ltr', fallback: 'en' },
  { code: 'fil', name: 'Filipino', flag: '🇵🇭', dir: 'ltr', fallback: 'en' }
];

export const DEFAULT_LOCALE = 'fr';

export const getLocaleFromBrowser = () => {
  const browserLang = navigator.language?.split('-')[0] || 'fr';
  
  // Check if it's a primary supported locale
  const primarySupported = SUPPORTED_LOCALES.find(l => l.code === browserLang);
  if (primarySupported) {
    return browserLang;
  }
  
  // Check if it's a worldwide locale with fallback
  const worldwideLocale = WORLDWIDE_LOCALES.find(l => l.code === browserLang);
  if (worldwideLocale) {
    // Return the fallback language but store the detected language for potential future use
    localStorage.setItem('drbooking_detected_locale', browserLang);
    return worldwideLocale.fallback || 'en';
  }
  
  // Default to French for unrecognized languages
  return DEFAULT_LOCALE;
};

export const getLocaleDirection = (locale) => {
  const localeInfo = WORLDWIDE_LOCALES.find(l => l.code === locale);
  return localeInfo?.dir || 'ltr';
};

export const getLocaleInfo = (locale) => {
  return WORLDWIDE_LOCALES.find(l => l.code === locale) || SUPPORTED_LOCALES[0];
};
